import { useCallback, useEffect, useRef, type Dispatch } from 'react'
import * as signalR from '@microsoft/signalr'
import type { GameHubEvents } from '@arabquiz/api-client'
import type { GameAction } from './gameReducer'
import {
  clearStoredPlayerSession,
  clearStoredSoloSession,
  decodeGameIdFromPlayerToken,
  writeStoredPlayerSession,
  writeStoredSoloSession,
} from './playerTokenStorage'

/**
 * SignalR bridge for the game hub. Owns the lifetime of a single
 * `HubConnection`, wires every server-emitted event onto the `dispatch`
 * pipeline, and returns a race-safe `invoke()` for outbound calls.
 *
 * The `startPromiseRef` exists because `connection.start()` is async but
 * React effects fire synchronously on mount — consumers (host pages, the
 * player join screen) routinely call `invoke('JoinAsHost', ...)` inside
 * their own mount effect, which would otherwise hit a still-`Connecting`
 * socket and silently no-op. Awaiting the start promise before checking
 * the connection state collapses that race without forcing every consumer
 * to thread a `isConnected` flag through the React tree.
 */
export function useGameHub(dispatch: Dispatch<GameAction>) {
  const connectionRef = useRef<signalR.HubConnection | null>(null)
  const startPromiseRef = useRef<Promise<void> | null>(null)
  // How to re-establish the SERVER-side association after the transport
  // auto-reconnects with a fresh connection id. Mobile browsers suspend a
  // backgrounded tab within seconds, dropping the websocket; `withAutomatic
  // Reconnect()` brings the SOCKET back but the hub has already run
  // `OnDisconnectedAsync` (player marked disconnected, host count drops to 0,
  // group membership lost). Replaying this intent on `onreconnected` re-binds
  // the new connection id to the existing player/host so the session resumes
  // seamlessly instead of the player going silent and the host seeing 0.
  const rejoinIntentRef = useRef<
    | { kind: 'host'; gameId: string }
    | { kind: 'player'; gameId: string; token: string }
    | { kind: 'solo'; gameId: string; token: string }
    | null
  >(null)

  useEffect(() => {
    // React StrictMode (dev) mounts effects twice: mount → cleanup → mount.
    // The cleanup below stops the connection while its negotiate request is
    // still in flight, which rejects `start()` with "The connection was
    // stopped during negotiation." `disposed` lets the start handler tell that
    // teardown-induced abort apart from a genuine connect failure.
    let disposed = false
    // Tracks whether a manual restart loop is already running for this effect
    // instance. Prevents concurrent restart attempts from onclose and
    // visibilitychange firing simultaneously.
    let isRestarting = false

    const connection = new signalR.HubConnectionBuilder()
      .withUrl('/hubs/game')
      // More forgiving retry schedule: up to ~2 min total before giving up and
      // firing onclose. The manual restart + visibilitychange path is the real
      // recovery for iOS backgrounding; this extends the automatic window.
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000, 60000])
      .build()

    const on = <TEventName extends keyof GameHubEvents>(
      eventName: TEventName,
      handler: GameHubEvents[TEventName],
    ) => {
      connection.on(eventName, handler as (...args: unknown[]) => void)
    }

    on('hostJoined', (payload) => {
      rejoinIntentRef.current = { kind: 'host', gameId: payload.gameId }
      dispatch({
        type: 'HOST_JOINED',
        gameId: payload.gameId,
        pin: payload.pin,
        playerCount: payload.playerCount,
      })
    })

    on('playerJoined', (payload) => {
      if (payload.token) {
        const gameId = decodeGameIdFromPlayerToken(payload.token)
        if (gameId) {
          writeStoredPlayerSession({
            gameId,
            token: payload.token,
            nickname: payload.nickname,
          })
          // Remember how to rebind this player if the transport drops (mobile
          // backgrounding) and auto-reconnects with a new connection id.
          rejoinIntentRef.current = { kind: 'player', gameId, token: payload.token }
        }

        dispatch({
          type: 'PLAYER_JOINED',
          gameId,
          playerId: payload.playerId,
          token: payload.token,
          nickname: payload.nickname,
          playerCount: payload.playerCount,
        })
        return
      }

      dispatch({
        type: 'PLAYER_COUNT_UPDATED',
        playerCount: payload.playerCount ?? 0,
      })
    })

    on('soloStarted', (payload) => {
      // Solo mode: the host is now also the sole player. Persist a SEPARATE
      // solo session (so a normal player session on this browser is untouched),
      // remember how to rebind on a transport drop, and reuse the existing
      // PLAYER_JOINED action — it already carries gameId/playerId/token/nickname
      // and leaves `isHost` as-is (true from the earlier JoinAsHost).
      writeStoredSoloSession({
        gameId: payload.gameId,
        token: payload.token,
        nickname: payload.nickname,
      })
      rejoinIntentRef.current = { kind: 'solo', gameId: payload.gameId, token: payload.token }
      dispatch({
        type: 'PLAYER_JOINED',
        gameId: payload.gameId,
        playerId: payload.playerId,
        token: payload.token,
        nickname: payload.nickname,
      })
    })

    on('questionStarted', (payload) => dispatch({ type: 'QUESTION_STARTED', payload }))

    on('answerReceived', (payload) => {
      // The hub emits two flavours of `answerReceived`:
      //   • player-scoped: carries `points` / `isCorrect` for the
      //     submitter and feeds the running score.
      //   • host-broadcast: carries `answerCount` (cumulative for the
      //     current question) so the projector can show "12 / 30".
      // The two shapes are disjoint in practice, so we branch instead
      // of merging — keeping the reducer's score-accumulation path
      // strictly opt-in for player events.
      if (payload.points !== undefined) {
        dispatch({
          type: 'ANSWER_RECEIVED',
          points: payload.points,
          isCorrect: payload.isCorrect ?? false,
        })
        return
      }
      if (typeof payload.answerCount === 'number') {
        dispatch({ type: 'ANSWER_COUNT_UPDATED', count: payload.answerCount })
      }
    })

    on('answersRevealed', (payload) => dispatch({ type: 'ANSWERS_REVEALED', payload }))
    on('alreadyAnswered', (payload) =>
      dispatch({ type: 'ALREADY_ANSWERED', questionIndex: payload.questionIndex }),
    )
    on('answerResult', (payload) =>
      dispatch({
        type: 'ANSWER_RESULT',
        questionIndex: payload.questionIndex,
        points: payload.points,
        isCorrect: payload.isCorrect,
        totalScore: payload.totalScore,
      }),
    )
    on('gameEnded', (payload) => {
      clearStoredPlayerSession()
      clearStoredSoloSession()
      dispatch({ type: 'GAME_ENDED', payload })
    })
    on('reconnected', (payload) =>
      dispatch({
        type: 'RECONNECTED',
        score: payload.score,
        state: payload.state,
        currentQuestionIndex: payload.currentQuestionIndex,
      }),
    )
    on('error', (message) => dispatch({ type: 'ERROR', message }))
    on('gameLost', (reason) => dispatch({ type: 'ERROR', message: reason }))
    on('rosterUpdated', (payload) =>
      dispatch({ type: 'ROSTER_UPDATED', players: payload.players, playerCount: payload.playerCount }),
    )
    on('sessionExpiring', (payload) =>
      dispatch({ type: 'SESSION_EXPIRING', respondWithinSeconds: payload.respondWithinSeconds }),
    )
    on('sessionExtended', () => dispatch({ type: 'SESSION_EXTENDED' }))
    on('hostDisconnected', () => dispatch({ type: 'HOST_DISCONNECTED' }))
    on('hostReconnected', () => dispatch({ type: 'HOST_RECONNECTED' }))

    // Shared helper: re-invoke the hub method that binds this client to an
    // existing game session. Called by both onreconnected (automatic transport
    // reconnect) and the manual restart path (onclose + visibilitychange).
    const replayRejoinIntent = () => {
      const intent = rejoinIntentRef.current
      if (!intent) return
      if (intent.kind === 'host') {
        void connection.invoke('JoinAsHost', intent.gameId).catch(() => {})
      } else if (intent.kind === 'solo') {
        void connection.invoke('ReconnectSolo', intent.gameId, intent.token).catch(() => {})
      } else {
        void connection.invoke('Reconnect', intent.gameId, intent.token).catch(() => {})
      }
    }

    // After the transport auto-reconnects (new connection id), the hub has no
    // memory of this client — replay the rejoin intent so the player/host is
    // re-bound to the existing game. Without this, a mobile tab backgrounded
    // for a few seconds comes back with a live socket but a dead session: the
    // player can't answer and the host's connected-count stays at 0.
    connection.onreconnected(() => {
      replayRejoinIntent()
    })

    // Manual restart loop: fires when the automatic reconnect schedule is
    // exhausted (onclose) or the tab becomes visible after the OS killed the
    // socket while backgrounded. Bounded retries with backoff; guards against
    // concurrent invocations via the `isRestarting` flag.
    const attemptManualRestart = async () => {
      if (isRestarting || disposed || !rejoinIntentRef.current) return
      isRestarting = true
      try {
        const delays = [0, 1000, 3000, 7000, 15000]
        for (const delay of delays) {
          if (disposed) return
          if (delay > 0) {
            await new Promise<void>((r) => setTimeout(r, delay))
          }
          if (disposed) return
          // If the connection recovered via another path (e.g. the automatic
          // reconnect fired during our backoff), stop the manual loop.
          if (connection.state !== signalR.HubConnectionState.Disconnected) return
          try {
            await connection.start()
            replayRejoinIntent()
            return
          } catch {
            // continue to next retry
          }
        }
      } finally {
        isRestarting = false
      }
    }

    // When automatic reconnect gives up entirely, the connection moves to
    // Disconnected and fires onclose. If we have a rejoin intent (i.e. the
    // user was in a game), kick off the manual restart loop.
    connection.onclose(() => {
      if (disposed || !rejoinIntentRef.current) return
      void attemptManualRestart()
    })

    // iOS Safari / PWA: the OS suspends backgrounded tabs, silently killing
    // the WebSocket. When the user returns, document.visibilityState flips to
    // 'visible'. If the connection is already Disconnected at that point (the
    // socket died while backgrounded and onclose may not have fired yet, or
    // the automatic retries all ran during the suspension), kick off the
    // manual restart here. This is the primary recovery path for iOS.
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'visible' &&
        connection.state === signalR.HubConnectionState.Disconnected &&
        !disposed &&
        rejoinIntentRef.current
      ) {
        void attemptManualRestart()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Capture the start promise so `invoke()` can await it before checking
    // the connection state. Genuine connect failures still flow through the
    // reducer so the UI can surface "connection lost" instead of hanging
    // silently — but a rejection caused by our own teardown (StrictMode's
    // double-mount, or a real unmount mid-handshake) is swallowed, since that
    // connection is already gone and the surviving one will connect on its own.
    startPromiseRef.current = connection.start().catch((err: unknown) => {
      if (disposed) {
        return
      }
      dispatch({ type: 'ERROR', message: String(err) })
    })

    connectionRef.current = connection

    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      void connection.stop()
    }
  }, [dispatch])

  const invoke = useCallback(async (method: string, ...args: unknown[]) => {
    // Record rejoin intent for the methods that establish a server-side
    // association, so `onreconnected` can replay it after a transport drop.
    if (method === 'JoinAsHost' && typeof args[0] === 'string') {
      rejoinIntentRef.current = { kind: 'host', gameId: args[0] }
    } else if (
      method === 'Reconnect' &&
      typeof args[0] === 'string' &&
      typeof args[1] === 'string'
    ) {
      rejoinIntentRef.current = { kind: 'player', gameId: args[0], token: args[1] }
    } else if (
      method === 'ReconnectSolo' &&
      typeof args[0] === 'string' &&
      typeof args[1] === 'string'
    ) {
      // A refresh-driven solo restore calls ReconnectSolo directly (no
      // soloStarted event), so record the intent here too — otherwise a later
      // transport drop would have nothing to replay.
      rejoinIntentRef.current = { kind: 'solo', gameId: args[0], token: args[1] }
    }
    // Two races stand between a caller and an invokable socket:
    //
    //   1. React runs *child* effects before *parent* effects, so a consumer
    //      that calls invoke() from its own mount effect (the host panel's
    //      `JoinAsHost`, the player `Reconnect`) can reach here before this
    //      provider's effect has created the connection — `startPromiseRef`
    //      is still null. And under StrictMode's double-mount the refs may
    //      briefly point at a superseded, already-stopped connection.
    //   2. Even once the connection exists, `start()` is async; firing before
    //      the handshake completes would no-op against a `Connecting` socket.
    //
    // A single read-and-bail (the previous behaviour) lost both races and left
    // callers stranded — e.g. the host stuck forever on "connecting". Instead
    // poll, bounded, until the *current* connection is actually Connected, then
    // invoke. If it never comes up, the start() failure is surfaced as an ERROR
    // elsewhere, so giving up quietly here is fine.
    const deadline = Date.now() + 5000
    while (Date.now() < deadline) {
      if (startPromiseRef.current) {
        await startPromiseRef.current
      }
      if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
        await connectionRef.current.invoke(method, ...args)
        return
      }
      await new Promise((resolve) => setTimeout(resolve, 20))
    }
  }, [])

  return { invoke, connection: connectionRef }
}
